import { collection, getDocs, doc, getDoc, updateDoc, setDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';
import type { BranchVendor, Branch, Vendor, VendorAssignment } from '../types';

/**
 * Fetch company ID for the current user
 */
export async function fetchCompanyId(uid: string): Promise<string | null> {
    const banksSnap = await getDocs(collection(db, 'banks'));
    for (const bankDoc of banksSnap.docs) {
        const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
        const posDoc = await getDoc(posDocRef);
        if (posDoc.exists()) {
            return bankDoc.id;
        }
    }
    return null;
}

/**
 * Fetch all branches for the company
 */
export async function fetchBranches(companyId: string, psoId?: string): Promise<Branch[]> {
    try {
        let branchesQuery;
        if (psoId) {
            branchesQuery = query(
                collection(db, 'banks', companyId, 'branch'),
                where('psoId', '==', psoId)
            );
        } else {
            branchesQuery = collection(db, 'banks', companyId, 'branch');
        }

        const branchesSnap = await getDocs(branchesQuery);
        return branchesSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Branch[];
    } catch (error) {
        console.error('Error fetching branches:', error);
        throw error;
    }
}

/**
 * Fetch all vendors from global companies collection (CCTV, BAMS, Guard Company)
 */
export async function fetchVendors(companyTypes?: string[]): Promise<Vendor[]> {
    try {
        const companiesSnap = await getDocs(collection(db, 'companies'));
        const vendors: Vendor[] = [];
        const allowedTypes = companyTypes || ['CCTV', 'BAMS', 'Guard Company'];

        companiesSnap.docs.forEach(companyDoc => {
            const companyData = companyDoc.data();
            if (allowedTypes.includes(companyData.companyType)) {
                vendors.push({
                    id: companyDoc.id,
                    companyId: companyData.companyId || companyDoc.id,
                    companyName: companyData.companyName,
                    companyType: companyData.companyType,
                    contactPersonName: companyData.contactPersonName,
                    contactEmail: companyData.contactEmail,
                    contactMobileNumber: companyData.contactMobileNumber,
                    ...companyData
                });
            }
        });

        return vendors;
    } catch (error) {
        console.error('Error fetching vendors:', error);
        throw error;
    }
}

/**
 * Fetch all CCTV vendors from global companies collection (for backward compatibility)
 */
export async function fetchCCTVVendors(): Promise<Vendor[]> {
    return fetchVendors(['CCTV']);
}

/**
 * Fetch branch-vendor assignments for all branches
 */
export async function fetchBranchVendorAssignments(
    companyId: string,
    branches: Branch[],
    vendors: Vendor[]
): Promise<BranchVendor[]> {
    try {
        const branchVendors: BranchVendor[] = [];

        // Create a map of vendors for quick lookup
        const vendorMap = new Map<string, Vendor>();
        vendors.forEach(vendor => {
            vendorMap.set(vendor.id, vendor);
        });

        // Process each branch
        for (const branch of branches) {
            const branchVendor: BranchVendor = {
                id: branch.id,
                branchId: branch.id,
                branchCode: branch.branchCode || 'N/A',
                branchName: branch.address || branch.name || 'N/A',
                branchAddress: branch.address,
            };

            // Check for CCTV vendor
            if (branch.cctvVendorId) {
                const vendor = vendorMap.get(branch.cctvVendorId);
                if (vendor) {
                    branchVendor.cctvVendor = {
                        vendorId: vendor.id,
                        vendorName: vendor.companyName,
                        vendorCompanyName: vendor.companyName,
                        vendorContactPerson: vendor.contactPersonName,
                        vendorEmail: vendor.contactEmail,
                        vendorMobile: vendor.contactMobileNumber,
                    };
                }
            }

            // Check for BAMS vendor
            if (branch.bamsVendorId) {
                const vendor = vendorMap.get(branch.bamsVendorId);
                if (vendor) {
                    branchVendor.bamsVendor = {
                        vendorId: vendor.id,
                        vendorName: vendor.companyName,
                        vendorCompanyName: vendor.companyName,
                        vendorContactPerson: vendor.contactPersonName,
                        vendorEmail: vendor.contactEmail,
                        vendorMobile: vendor.contactMobileNumber,
                    };
                }
            }

            // Check for Guard Company vendor
            if (branch.guardVendorId) {
                const vendor = vendorMap.get(branch.guardVendorId);
                if (vendor) {
                    branchVendor.guardVendor = {
                        vendorId: vendor.id,
                        vendorName: vendor.companyName,
                        vendorCompanyName: vendor.companyName,
                        vendorContactPerson: vendor.contactPersonName,
                        vendorEmail: vendor.contactEmail,
                        vendorMobile: vendor.contactMobileNumber,
                    };
                }
            }

            branchVendors.push(branchVendor);
        }

        return branchVendors;
    } catch (error) {
        console.error('Error fetching branch-vendor assignments:', error);
        throw error;
    }
}

/**
 * Assign or update vendor for a branch (supports multiple vendors - one per type)
 */
export async function assignVendorToBranch(
    companyId: string,
    branchId: string,
    vendorAssignments: {
        cctvVendorId?: string | null;
        bamsVendorId?: string | null;
        guardVendorId?: string | null;
    }
): Promise<void> {
    try {
        const uid = auth.currentUser?.uid;
        if (!uid) {
            throw new Error('User not authenticated');
        }

        const branchRef = doc(db, 'banks', companyId, 'branch', branchId);
        const updateData: any = {
            updatedAt: serverTimestamp(),
            updatedBy: uid,
        };

        // Update each vendor type independently
        if (vendorAssignments.cctvVendorId !== undefined) {
            updateData.cctvVendorId = vendorAssignments.cctvVendorId || null;
        }
        if (vendorAssignments.bamsVendorId !== undefined) {
            updateData.bamsVendorId = vendorAssignments.bamsVendorId || null;
        }
        if (vendorAssignments.guardVendorId !== undefined) {
            updateData.guardVendorId = vendorAssignments.guardVendorId || null;
        }

        await updateDoc(branchRef, updateData);
    } catch (error) {
        console.error('Error assigning vendor to branch:', error);
        throw error;
    }
}

/**
 * Get vendor details by ID
 */
export async function getVendorById(vendorId: string): Promise<Vendor | null> {
    try {
        const vendorDoc = await getDoc(doc(db, 'companies', vendorId));
        if (vendorDoc.exists()) {
            const vendorData = vendorDoc.data();
            return {
                id: vendorDoc.id,
                companyId: vendorData.companyId || vendorDoc.id,
                companyName: vendorData.companyName,
                companyType: vendorData.companyType,
                contactPersonName: vendorData.contactPersonName,
                contactEmail: vendorData.contactEmail,
                contactMobileNumber: vendorData.contactMobileNumber,
                ...vendorData
            } as Vendor;
        }
        return null;
    } catch (error) {
        console.error('Error fetching vendor:', error);
        throw error;
    }
}

